import {useCallback, useEffect, useId, useRef, useState} from 'react';

import {usePanel} from '../app/PanelProvider.js';
import {useReauth} from '../auth/ReauthContext.js';
import {useWebAuthn} from '../auth/WebAuthnContext.js';
import {cancelamentoDoUsuario} from '../auth/webauthn.js';

/**
 * Cerimônia de reautenticação, oferecida quando uma ação sensível volta com
 * "confirme o passkey" (403 de `requireRecentReauth`).
 *
 * Duas decisões de comportamento:
 *
 * 1. **A ação recusada NÃO se repete sozinha.** Depois do passkey confirmado,
 *    o diálogo pergunta. Entre a recusa e a confirmação o dono pensou, e uma
 *    ação irreversível reexecutada por inércia é exatamente o que a
 *    reautenticação existe para impedir.
 * 2. **Cancelar a cerimônia é caminho normal, não erro morto.** O diálogo
 *    mostra o que houve e deixa tentar de novo ou fechar. A tela por trás
 *    continua inteira.
 * 3. **Sempre existe saída.** "Agora não" e a tecla Esc fecham em QUALQUER
 *    fase, inclusive durante a cerimônia. O diálogo é modal e cobre o painel
 *    inteiro; se o fechar dependesse de a chamada de rede terminar, uma conexão
 *    pendurada (que não responde nem rejeita, e as chamadas daqui não têm
 *    prazo) trancaria o painel atrás do backdrop até recarregar a página. Só o
 *    "Confirmar passkey" fica desabilitado na cerimônia, para não disparar duas.
 *
 * O componente rende nada quando não há pedido pendente, então ele não
 * existe no DOM do modo fixture e o pixel aprovado não vê um byte dele.
 */
export const ReauthDialog = () => {
  const {pedido, fechar} = useReauth();
  const {dataSource} = usePanel();
  const webauthn = useWebAuthn();
  const tituloId = useId();

  const [fase, setFase] = useState<'oferta' | 'cerimonia' | 'confirmar'>(
    'oferta',
  );
  const [problema, setProblema] = useState<string | null>(null);

  /**
   * Geração da cerimônia em curso.
   *
   * Fechar durante a cerimônia não cancela a chamada que já saiu. Sem este
   * contador, uma resposta atrasada de uma cerimônia abandonada chegaria depois
   * e mudaria a fase de um pedido NOVO, que é o diálogo mentindo sobre qual
   * ação foi confirmada. Cada início e cada fechamento incrementam; o resultado
   * só vale se a geração ainda for a dele.
   */
  const geracao = useRef(0);

  // O diálogo renasce limpo a cada pedido novo: estado de uma recusa antiga
  // vazando para a próxima confundiria a cerimônia atual.
  const pedidoAnterior = useRef(pedido);
  useEffect(() => {
    if (pedido !== pedidoAnterior.current) {
      pedidoAnterior.current = pedido;
      geracao.current += 1;
      setFase('oferta');
      setProblema(null);
    }
  }, [pedido]);

  const auth = dataSource.auth;

  const fecharEAbandonar = useCallback(() => {
    // Incrementa antes de fechar: o que estiver em voo vira resultado órfão.
    geracao.current += 1;
    setFase('oferta');
    setProblema(null);
    fechar();
  }, [fechar]);

  // Esc fecha, como em todo diálogo modal. Este não é um <dialog> nativo (o
  // backdrop e o foco são nossos), então a tecla precisa ser tratada na mão.
  useEffect(() => {
    if (!pedido) {
      return;
    }
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        fecharEAbandonar();
      }
    };
    globalThis.addEventListener?.('keydown', aoTeclar);
    return () => {
      globalThis.removeEventListener?.('keydown', aoTeclar);
    };
  }, [fecharEAbandonar, pedido]);

  const confirmar = useCallback(() => {
    if (!auth) {
      return;
    }
    geracao.current += 1;
    const minhaGeracao = geracao.current;
    setFase('cerimonia');
    setProblema(null);
    void (async () => {
      const opcoes = await auth.reauthOptions();
      if (opcoes.ceremony === 'registration') {
        // Lista de permissão: reauth é sempre assertion. Um formato novo cai
        // aqui e é recusado, em vez de entrar numa cerimônia errada.
        throw new Error('A API devolveu uma cerimônia que não é de reauth.');
      }
      const resposta = await webauthn.authenticate(opcoes.options);
      await auth.reauthVerify({
        ceremonyId: opcoes.ceremonyId,
        response: resposta,
      });
    })()
      .then(() => {
        if (geracao.current !== minhaGeracao) {
          return;
        }
        setFase('confirmar');
      })
      .catch((erro: unknown) => {
        if (geracao.current !== minhaGeracao) {
          return;
        }
        setFase('oferta');
        setProblema(
          cancelamentoDoUsuario(erro)
            ? 'Cerimônia cancelada. Nada foi confirmado; tente de novo quando quiser.'
            : 'Não foi possível confirmar o passkey. Tente de novo em instantes.',
        );
      });
  }, [auth, webauthn]);

  const repetir = useCallback(() => {
    const retry = pedido?.retry ?? null;
    geracao.current += 1;
    fechar();
    // Fechar antes de repetir: se a repetição falhar de novo, o pedido novo
    // abre um diálogo novo, limpo, em vez de reaproveitar este pela metade.
    if (retry) {
      retry();
    }
  }, [fechar, pedido]);

  if (!pedido || !auth) {
    return null;
  }

  return (
    <div className="dm-dialog-backdrop">
      <div
        className="dm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
      >
        <h2 id={tituloId}>Confirmar com passkey</h2>

        {fase === 'confirmar' ? (
          <>
            <p className="dm-subtitle">
              Passkey confirmada. A confirmação vale por 5 minutos.
            </p>
            <div className="dm-actions">
              {pedido.retry ? (
                <button
                  type="button"
                  className="dm-action dm-action-primary"
                  onClick={repetir}
                >
                  Repetir a ação
                </button>
              ) : null}
              <button
                type="button"
                className="dm-action"
                onClick={fecharEAbandonar}
              >
                Fechar
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="dm-subtitle">{pedido.message}</p>
            {problema ? (
              <div className="dm-alert" role="alert">
                {problema}
              </div>
            ) : null}
            <div className="dm-actions">
              <button
                type="button"
                className="dm-action dm-action-primary"
                onClick={confirmar}
                disabled={fase === 'cerimonia'}
              >
                {fase === 'cerimonia'
                  ? 'Aguardando a passkey…'
                  : 'Confirmar passkey'}
              </button>
              {/* Sem `disabled`: esta é a única saída do diálogo modal, e ela
                  não pode depender de uma chamada de rede terminar. */}
              <button
                type="button"
                className="dm-action"
                onClick={fecharEAbandonar}
              >
                Agora não
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
